import { ago } from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import type { WorkflowRunDisplayState } from "components/workflow/workflow-run-status";
import moment from "moment";
import { joinWithAnd } from "util/text";

export type PartnerSyncStatus = inferProcedureOutput<
    AppRouter["homeCareLeadPartnerSend"]["syncNow"]
>;

export type PartnerSyncCurrent = NonNullable<PartnerSyncStatus["current"]>;

export type PartnerSyncHistoryEntry = PartnerSyncStatus["history"][number];

type PartnerSyncSyncing = Extract<PartnerSyncCurrent, { state: "syncing" }>;

const SYNC_DUE_POLL_MS = 5 * 1000;

const SYNC_IDLE_POLL_MS = 60 * 1000;

const SYNC_DUE_FAST_POLL_WINDOW_MS = 3 * 60 * 1000;

export const partnerSyncPollIntervalOf = (
    sync: PartnerSyncStatus | null,
    nowMs: number,
): number | false => {
    if (!sync) {
        return false;
    }
    if (sync.current?.state !== "syncing") {
        return SYNC_IDLE_POLL_MS;
    }
    const untilDueMs = Date.parse(sync.current.scheduledAt) - nowMs;
    if (untilDueMs > 0) {
        return Math.min(SYNC_IDLE_POLL_MS, Math.max(SYNC_DUE_POLL_MS, untilDueMs));
    }
    return -untilDueMs < SYNC_DUE_FAST_POLL_WINDOW_MS
        ? SYNC_DUE_POLL_MS
        : SYNC_IDLE_POLL_MS;
};

const isDue = (scheduledAt: string, now: Date): boolean =>
    Date.parse(scheduledAt) <= now.getTime();

const relativeTo = (at: string, now: Date): string => moment(at).from(now);

const syncingHeadlineOf = (current: PartnerSyncSyncing, now: Date): string => {
    const verb = current.retrying ? "Retrying" : "Syncing";
    return isDue(current.scheduledAt, now)
        ? `${verb} now`
        : `${verb} ${relativeTo(current.scheduledAt, now)}`;
};

export const headlineOf = (current: PartnerSyncCurrent, now: Date): string => {
    switch (current.state) {
        case "syncing":
            return syncingHeadlineOf(current, now);
        case "synced":
            return `Synced ${relativeTo(current.finishedAt, now)}`;
        case "failed":
            return `Sync failed ${relativeTo(current.finishedAt, now)}`;
        default: {
            const exhaustiveCheck: never = current;
            throw new Error(`Unhandled sync state: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

export const iconStateOf = (current: PartnerSyncCurrent): WorkflowRunDisplayState => {
    switch (current.state) {
        case "syncing":
            return "scheduled";
        case "synced":
            return "success";
        case "failed":
            return "error";
        default: {
            const exhaustiveCheck: never = current;
            throw new Error(`Unhandled sync state: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

const syncingDetailOf = (
    current: PartnerSyncSyncing,
    partnerName: string,
    now: Date,
): string => {
    const when = isDue(current.scheduledAt, now) ? "now" : ago(current.scheduledAt);
    return current.retrying
        ? `${partnerName} could not be reached. Trying again ${when}.`
        : `Changes go to ${partnerName} ${when}.`;
};

export const summaryDetailOf = (
    current: PartnerSyncCurrent,
    partnerName: string,
    now: Date,
): string => {
    switch (current.state) {
        case "syncing":
            return syncingDetailOf(current, partnerName, now);
        case "synced":
            return `Last synced ${ago(current.finishedAt)}.`;
        case "failed":
            return current.message ?? `The last sync failed ${ago(current.finishedAt)}.`;
        default: {
            const exhaustiveCheck: never = current;
            throw new Error(`Unhandled sync state: ${JSON.stringify(exhaustiveCheck)}`);
        }
    }
};

const finishedEntryTitleOf = (
    entry: PartnerSyncHistoryEntry,
    partnerName: string,
): string => {
    if (entry.kind === "send") {
        return entry.status === "failed" && entry.fields.length === 0
            ? "Send failed"
            : `Sent to ${partnerName}`;
    }
    if (entry.status === "failed") {
        return "Sync failed";
    }
    return entry.fields.length > 0
        ? `Updated ${joinWithAnd(entry.fields)}`
        : "Already up to date";
};

export const entryTitleOf = (
    entry: PartnerSyncHistoryEntry,
    partnerName: string,
    current: PartnerSyncCurrent | null,
): string => {
    switch (entry.status) {
        case "pending":
            return isShownInSummary(entry, current) ? "Did not start" : "Waiting to sync";
        case "running":
            return "Syncing";
        case "succeeded":
        case "failed":
            return finishedEntryTitleOf(entry, partnerName);
        default: {
            const exhaustiveCheck: never = entry.status;
            throw new Error(`Unhandled sync status: ${exhaustiveCheck}`);
        }
    }
};

export const entryTimeOf = (entry: PartnerSyncHistoryEntry): string =>
    entry.status === "pending"
        ? `scheduled for ${ago(entry.scheduledAt, "")}`
        : ago(entry.finishedAt ?? entry.startedAt ?? entry.scheduledAt, "");

export const entryIconStateOf = (
    entry: PartnerSyncHistoryEntry,
    current: PartnerSyncCurrent | null,
): WorkflowRunDisplayState => {
    switch (entry.status) {
        case "pending":
            return isShownInSummary(entry, current) ? "error" : "scheduled";
        case "running":
            return "queued";
        case "succeeded":
            return "success";
        case "failed":
            return "error";
        default: {
            const exhaustiveCheck: never = entry.status;
            throw new Error(`Unhandled sync status: ${exhaustiveCheck}`);
        }
    }
};

export const isShownInSummary = (
    entry: PartnerSyncHistoryEntry,
    current: PartnerSyncCurrent | null,
): boolean => current?.state === "failed" && entry.id === current.syncId;

export const historyDetailOf = (
    entry: PartnerSyncHistoryEntry,
    current: PartnerSyncCurrent | null,
): string | null => (isShownInSummary(entry, current) ? null : entry.errorMessage);

export const syncNowLabelOf = (current: PartnerSyncCurrent | null): string =>
    current?.state === "failed" ? "Try again" : "Sync now";
